'use client';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to persist state in sessionStorage.
 * Returns undefined initially while loading, then the parsed value or null if not set.
 */
export function useSessionStorageState<T>(key: string): [T | null | undefined, (value: T) => void] {
   const [value, setValue] = useState<T | null | undefined>();

   useEffect(() => {
      try {
         // eslint-disable-next-line @ifixit/no-window-storage
         const storedValue = sessionStorage.getItem(key);
         if (storedValue == null) {
            setValue(null);
         } else {
            setValue(JSON.parse(storedValue));
         }
      } catch (error) {
         console.warn(`Failed to read from sessionStorage for key "${key}":`, error);
         setValue(null);
      }
   }, [key]);

   const setStoredValue = useCallback(
      (newValue: T | null) => {
         setValue(newValue);
         try {
            // eslint-disable-next-line @ifixit/no-window-storage
            sessionStorage.setItem(key, JSON.stringify(newValue));
         } catch (error) {
            console.warn(`Failed to write to sessionStorage for key "${key}":`, error);
         }
      },
      [key]
   );

   return [value, setStoredValue];
}
